<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import CrmTopbar from './components/CrmTopbar.vue';
import CrmKanbanColumn from './components/CrmKanbanColumn.vue';
import CrmOpportunityPanel from './components/CrmOpportunityPanel.vue';
import CrmOpportunityForm from './components/CrmOpportunityForm.vue';
import CrmFunnelSettingsModal from './components/CrmFunnelSettingsModal.vue';

const { t } = useI18n();
const crmStore = useCrmStore();

const showCreateForm = ref(false);
const showSettings = ref(false);
const newOpportunityStageId = ref(null);
const editOpportunityId = ref(null);

onMounted(() => {
  crmStore.fetchFunnels();
});

const columns = computed(() =>
  crmStore.stages.map(stage => ({
    stage,
    opportunities: crmStore.opportunitiesByStage[stage.id] || [],
  }))
);

const handleAddOpportunity = stageId => {
  newOpportunityStageId.value = stageId;
  showCreateForm.value = true;
};

const handleOpportunityClick = opportunityId => {
  crmStore.selectOpportunity(opportunityId);
};

const handleEditOpportunity = id => {
  editOpportunityId.value = id;
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <CrmTopbar
      @create-opportunity="showCreateForm = true"
      @open-settings="showSettings = true"
    />
    <div
      v-if="crmStore.isLoading && crmStore.stages.length === 0"
      class="flex-1 flex items-center justify-center text-n-slate-10 text-sm"
    >
      {{ t('CRM.BOARD.LOADING') }}
    </div>
    <div
      v-else-if="!crmStore.currentFunnelId"
      class="flex-1 flex items-center justify-center text-n-slate-10 text-sm"
    >
      {{ t('CRM.BOARD.NO_FUNNEL') }}
    </div>
    <div v-else class="flex-1 flex overflow-hidden">
      <div class="flex-1 flex gap-4 p-4 overflow-x-auto overflow-y-hidden">
        <CrmKanbanColumn
          v-for="col in columns"
          :key="col.stage.id"
          :stage="col.stage"
          :opportunities="col.opportunities"
          @opportunity-click="handleOpportunityClick"
          @add-opportunity="handleAddOpportunity"
        />
      </div>
      <CrmOpportunityPanel @edit="handleEditOpportunity" />
      <CrmOpportunityForm
        v-if="showCreateForm"
        :key="0"
        :stage-id="newOpportunityStageId"
        @close="
          showCreateForm = false;
          newOpportunityStageId = null;
        "
      />
      <CrmOpportunityForm
        v-if="editOpportunityId"
        :key="editOpportunityId"
        :opportunity-id="editOpportunityId"
        @close="editOpportunityId = null"
      />
    </div>
    <CrmFunnelSettingsModal v-if="showSettings" @close="showSettings = false" />
  </section>
</template>
